import * as React from 'react';
import * as ReactDOM from 'react-dom';
import cn from 'classnames';
import { Coords } from 'google-map-react';

import Map, { MarkerProps } from '../google-map/MapWithMarkers';
import { MapContext } from '../google-map/MapContext';
import { CSNSearchVenueData } from '@weddingspot/ws-api-client';
import MapAnnotation from '../google-map/MapAnnotation';
import MapZoomControl from '../google-map/MapZoomControl';
import MapTypeControl from '../google-map/MapTypeControl';

const styles = require('./HotelsMap.scss');

interface VenueMarkerProps extends MarkerProps {
    focused: boolean;
}

const SearchLocationMarker = (props: VenueMarkerProps) => {
    return (
        <MapAnnotation
            key={`${props.id}`} // Has to be a string for some reason
            lat={props.lat}
            lng={props.lng}
            focusedVenueKey={null}
            venueId={props.id as number}
        />
    );
};

const VenueMarker = (props: VenueMarkerProps) => {
    const { focused } = props;
    return (
        <div className={cn(styles.circleMarker, { [styles.circleMarkerFocused]: focused })}>
            {focused && <div className={styles.circleMarkerInner} />}
        </div>
    );
};

interface HotelsMapProps {
    isLoading: boolean;
    searchLocation?: Coords;
    hotels: CSNSearchVenueData[];
    onMarkerClick: (id: string) => void;
    focusedHotel?: string;
}

const SEARCH_LOCATION_ID = '_searchLocation';

export const HotelsMap = (props: HotelsMapProps) => {
    const { isLoading, hotels, searchLocation, onMarkerClick, focusedHotel } = props;

    const [mapCenter, setMapCenter] = React.useState<Coords>();
    const [mapZoom, setMapZoom] = React.useState<number>();
    const [markerProps, setMarkerProps] = React.useState<MarkerProps[]>([]);

    React.useEffect(() => {
        const _markerProps = hotels
            .filter((h) => !!h.geographicLocation)
            .map((h) => {
                const geographicLocation = h.geographicLocation as { lat: number; lon: number };
                return {
                    id: h.id,
                    lat: geographicLocation.lat,
                    lng: geographicLocation.lon,
                    focused: h.id === focusedHotel,
                };
            });

        if (searchLocation) {
            _markerProps.push({
                id: SEARCH_LOCATION_ID,
                lat: searchLocation.lat,
                lng: searchLocation.lng,
                focused: false,
            });
        }

        setMarkerProps(_markerProps);
    }, [hotels, focusedHotel]);

    const renderMarker = (props: VenueMarkerProps, idx: number) => {
        if (props.id === SEARCH_LOCATION_ID) {
            return <SearchLocationMarker key={props.id} {...props} />;
        }

        return <VenueMarker key={`${props.id}.${idx}`} {...props} />;
    };

    const handleOnLoad = (map: google.maps.Map) => {
        // place custom controls on google map
        const controls: [JSX.Element, google.maps.ControlPosition][] = [
            [<MapTypeControl key='MapType' onChange={(mapType) => map.setMapTypeId(mapType)} />, google.maps.ControlPosition.TOP_LEFT],
            [
                // 9 is the middle value
                <MapZoomControl key='MapZoom' onChange={(incr: 1 | -1) => map.setZoom((map.getZoom() || 9) + incr)} />,
                google.maps.ControlPosition.LEFT_TOP,
            ],
            /*
            [
                <CurrentLocationControl key="CurrentLocation" onClick={() => alert('click!')}/>,
                google.maps.ControlPosition.TOP_RIGHT
            ]
            */
        ];

        controls.map(([element, position]) => {
            const controlDiv = document.createElement('div');
            ReactDOM.render(element, controlDiv);
            map.controls[position].push(controlDiv);
        });
    };

    return (
        <MapContext.Provider
            value={{
                mapCenter: mapCenter,
                mapZoom: mapZoom,
                setMapCenter: setMapCenter,
                setMapZoom: setMapZoom,
            }}
        >
            <Map
                isLoading={isLoading}
                markerProps={markerProps}
                onMarkerClick={(key, props) => onMarkerClick(props.id as string)}
                renderMarker={renderMarker}
                onGoogleMapLoad={handleOnLoad}
                googleMapsProps={{
                    options: {
                        zoomControl: false,
                        fullscreenControl: false,
                        clickableIcons: false,
                    },
                }}
            />
        </MapContext.Provider>
    );
};
